import { isEqual } from "lodash";
import { memo, useState, useEffect } from "react";
import { View, TouchableOpacity } from "react-native";
import { Divider, Text } from "react-native-paper";

import { Modal, Button, CheckboxItem, IconButton } from "../../components";

interface FilterModalProps {
  creditTypeFilters: { [key: string]: boolean };
  seriesFilters: { [key: string]: boolean };
  onChangeCreditType(value: string): void;
  onChangeSeries(value: string): void;
  onReset(): void;
  onDismiss(): void;
  visible: boolean;
  videoSeriesOptions: string[];
}

const sectionLabels = {
  creditType: "PDR Credit Type",
  series: "Series",
};

type DataSection = { type: "section"; label: string };
type DataRow = {
  type: "row";
  attribute: string;
  value: string;
  label: string;
  checked: boolean;
};
type ListData = DataSection | DataRow;

const Section = ({ label, index }: { label: string; index: number }) => (
  <>
    {index ? <Divider className="bg-sky-lighter h-1.5 mt-6" /> : null}
    <Text className="text-large-normal text-ink-darkest font-700 px-6 mb-2 pt-6">{label}</Text>
  </>
);

const Checkbox = ({ attribute, value, label, checked, onChange }: DataRow & { onChange(a: string, v: string): void }) => (
  <CheckboxItem
    label={label}
    key={[attribute, value].join("-")}
    labelStyle="text-regular-tight pl-2"
    position="trailing"
    value={checked}
    onChange={() => {
      onChange(attribute, value);
    }}
  />
);

const Item = memo(
  ({ item, index, onChange }: { item: ListData; onChange(a: string, v: string): void; index: number }) => {
    switch (item.type) {
      case "section":
        return <Section {...{ ...item, index }} />;
      case "row":
        return <Checkbox {...{ ...item, onChange }} />;
    }
  },
  ({ item: itemA }, { item: itemB }) => isEqual(itemA, itemB)
);

const buildData = (
  creditTypeFilters: { [key: string]: boolean },
  seriesFilters: { [key: string]: boolean },
  videoSeriesOptions: string[]
) => {
  const creditTypeOptions = [
    { value: "EDUCATION", label: "Education" },
    { value: "PLAYER_ENGAGEMENT", label: "Player Engagement" },
    { value: "MEETINGS", label: "Meetings" },
    { value: "NATIONAL_SURVEYS", label: "National Surveys" },
  ];
  const data: ListData[] = [
    { type: "section", label: sectionLabels.creditType },
    ...creditTypeOptions.map(({ value, label }) => ({
      type: "row" as const,
      attribute: "creditType",
      value,
      label,
      checked: creditTypeFilters[value] || false,
    })),
    { type: "section", label: sectionLabels.series },
    ...videoSeriesOptions.map((value) => ({
      type: "row" as const,
      attribute: "series",
      value,
      label: value,
      checked: seriesFilters[value] || false,
    })),
  ];
  return data;
};

const keyExtractor = (item: ListData) => {
  switch (item.type) {
    case "section":
      return ["section", item.label].join("-");
    case "row":
      return ["row", item.attribute, item.value].join("-");
  }
};

export default function FilterModal({
  creditTypeFilters,
  seriesFilters,
  onChangeCreditType,
  onChangeSeries,
  onDismiss,
  visible,
  onReset,
  videoSeriesOptions,
}: FilterModalProps) {
  const [localCreditTypeFilters, setLocalCreditTypeFilters] = useState(creditTypeFilters);
  const [localSeriesFilters, setLocalSeriesFilters] = useState(seriesFilters);

  useEffect(() => {
    setLocalCreditTypeFilters(creditTypeFilters);
    setLocalSeriesFilters(seriesFilters);
  }, [creditTypeFilters, seriesFilters]);

  const data: ListData[] = buildData(localCreditTypeFilters, localSeriesFilters, videoSeriesOptions);

  const onChange = (attribute: string, value: string) => {
    if (attribute === "creditType") {
      setLocalCreditTypeFilters((prev) => ({ ...prev, [value]: !prev[value] }));
    } else if (attribute === "series") {
      setLocalSeriesFilters((prev) => ({ ...prev, [value]: !prev[value] }));
    }
  };

  const handleApply = () => {
    Object.entries(localCreditTypeFilters).forEach(([key, value]) => {
      if (value !== creditTypeFilters[key]) {
        onChangeCreditType(key);
      }
    });
    Object.entries(localSeriesFilters).forEach(([key, value]) => {
      if (value !== seriesFilters[key]) {
        onChangeSeries(key);
      }
    });
    onDismiss();
  };

  const handleReset = () => {
    setLocalCreditTypeFilters({});
    setLocalSeriesFilters({});
    onReset();
  };

  const renderItem = ({ item, index }: { item: ListData; index: number }) => <Item {...{ item, index, onChange }} />;

  return (
    <Modal
      fullScreen
      {...{ visible, onDismiss }}
      flatListProps={{ data, renderItem, keyExtractor }}
      HeaderComponent={
        <View className="flex-row items-center justify-between bg-white rounded-t-2xl border-b border-b-sky mb-2">
          <View className="flex-1">
            <IconButton icon="x" onPress={onDismiss} />
          </View>
          <Text className="flex-2 text-regular text-ink-darkest text-center font-500">Filters</Text>
          <TouchableOpacity className="flex-1 flex-row items-center justify-end" onPress={handleReset}>
            <Text className="mr-6 text-primary text-large-normal">Reset</Text>
          </TouchableOpacity>
        </View>
      }
      FooterComponent={
        <View className="px-6 pt-6 pb-10 border-t border-sky">
          <Button onPress={handleApply} mode="contained">
            Apply Filters
          </Button>
        </View>
      }
    />
  );
}
